import { storeToRefs } from 'pinia'

import { AuthService } from '@common/auth-layer/services/auth.service'
import {
  createAdminPreviewEntryNonce,
  isCurrentAdminPreviewEntry,
  resolveAdminPreviewExpiry,
  writeAdminPreviewAgencyToken,
  writeAdminPreviewTabRecord,
  writeAdminV2PreviewEnvelope,
  type AdminV2PreviewEnvelope,
} from '@common/core-layer/utils/admin-preview-token.utils'
import { writeAdminPreviewMarkerCookie } from '@common/core-layer/utils/admin-preview-cookie.utils'

import { useCoursesAdminPreviewStore } from '../stores/courses-admin-preview.ui-store'

import type { CoursesAdminPreviewExchangeOptions } from '../types/courses-admin-preview.types'

/**
 * Single entry point for the courses admin-preview session. Use instead of the
 * store directly (mirrors the beta-preview-layer pattern).
 */
export const useCoursesAdminPreview = () => {
  const store = useCoursesAdminPreviewStore()
  const {
    isAdminPreview,
    agencyBearerToken,
    previewLocationId,
    builderMode,
    templateId,
    companyId,
    hasToken,
    adminSession,
    adminAccessToken,
    authMode,
    sessionStatus,
  } = storeToRefs(store)

  /**
   * IAM v2 admin-preview token exchange (mirrors membership-hmi-preview `signInAsAdminV2`).
   * Exchanges the `v2.` builder token for a location-scoped IAM v2 admin session, stores it as
   * the `acatv2` envelope, and returns it on success (null on failure). No Firebase sign-in here.
   *
   * `options.entryNonce` ties the exchange to the preview entry that started it (this tab's
   * `cp-admin-preview` record nonce): when a newer entry or a preview exit replaced the record while
   * the request was in flight, the result is discarded and nothing is written.
   */
  async function exchangeAdminPreviewSession(
    agencyToken: string,
    locationId: string,
    companyId?: string | null,
    options: CoursesAdminPreviewExchangeOptions = {}
  ): Promise<AdminV2PreviewEnvelope | null> {
    try {
      // Served by the membership backend, not the clientclub base.
      const authService = new AuthService(locationId, 'MEMBERSHIP_SERVER_URL')
      // `companyId` (from the `company_id` preview URL param) lets membership-backend fall back to
      // an agency-user lookup by email + company (mirrors membership-hmi-preview PR #1329).
      const { data } = await authService.loginWithAdminTokenV2({ agencyToken, locationId, companyId })
      const accessToken = data?.accessToken
      if (!accessToken) return null
      // Superseded while in flight (newer entry / exit cleared this tab's record): write nothing.
      if (options.entryNonce && !isCurrentAdminPreviewEntry(options.entryNonce)) return null
      const envelope: AdminV2PreviewEnvelope = { ...data, token: accessToken }
      const sessionLocationId = envelope.locationId ?? locationId
      store.setAdminSession(envelope)
      // sessionStorage (client-only) so the shared BaseService reads the admin session; too large
      // for a cookie — see @common/core-layer/utils/admin-preview-token.utils.
      writeAdminV2PreviewEnvelope(envelope)
      // Per-tab sessionStorage so the raw agency token can be replayed to the federated remote on
      // reload (it runs its own sign-in exchange and can't use the exchanged admin access token).
      // Cleared when leaving /courses (see the `00.` middleware).
      writeAdminPreviewAgencyToken(agencyToken)
      // This tab's preview record: the only thing that lets BaseService use the `acatv2` session, so
      // the admin token never applies in another tab. Server-minted location/expiry are authoritative.
      writeAdminPreviewTabRecord({
        mode: 'acatv2',
        locationId: sessionLocationId,
        exp: resolveAdminPreviewExpiry(envelope.tokenExpirationTime),
        nonce: options.entryNonce ?? createAdminPreviewEntryNonce(),
      })
      // Tiny non-sensitive marker cookie (locationId + expiry, NO token) so SSR routing guards
      // recognise the preview on a full reload, since sessionStorage is server-invisible.
      writeAdminPreviewMarkerCookie({ locationId: sessionLocationId, expiresAt: envelope.tokenExpirationTime })
      // The learner `cat` / `catv2` session is deliberately left intact: admin credentials live under
      // their own keys, the `/courses` guards bypass learner auth during a preview, and BaseService
      // only uses the admin token for courses/membership calls — so leaving the preview restores the
      // learner exactly as it was.
      return envelope
    } catch {
      // Do not log the request error object: HTTP client errors may retain the agency token
      // in request config/body metadata.
      console.error('[courses-admin-preview] IAM v2 token exchange failed')
      return null
    }
  }

  return {
    isActive: isAdminPreview,
    agencyBearerToken,
    previewLocationId,
    builderMode,
    templateId,
    companyId,
    hasToken,
    adminSession,
    adminAccessToken,
    authMode,
    sessionStatus,
    setAdminPreviewContext: store.setAdminPreviewContext,
    setAdminPreviewToken: store.setAdminPreviewToken,
    setAdminSession: store.setAdminSession,
    setAuthMode: store.setAuthMode,
    setSessionStatus: store.setSessionStatus,
    clearAdminPreview: store.clearAdminPreview,
    exchangeAdminPreviewSession,
  }
}
